import { Area, AreaSubTypeEnum, DeviceEnabledType, getTTLSeconds, UserAttributeDefs, ValidModelType } from '@iotv/datamodel-core'
import { Milliseconds, PathDirection, Position, TimedPosition } from '@iotv/iotv-v3-types'
import { lineString, point, polygon } from '@turf/helpers'
import { booleanPointInPolygon, pointToLineDistance } from '@turf/turf'
import { Feature, GeoJsonProperties, LineString } from 'geojson'
import { DeadZone, DeadZoneType } from './DeadZone'
import { EffluentPath } from './EffluentPath'
import { SoilMoistureArea } from './SoilMoistureArea'
import { SpreadingEventType } from './SpreadingEvent'
import { EffluentSpreaderHelper } from '../utilityFns'

const typeName = 'EffluentTracker'
const dev = process.env.REACT_APP_DEBUG
const debug = dev && false

export const DEFAULT_RANGE = 20
export const ACCEPTABLE_COVERAGE = 0
export interface RunStats {
  distance: number
  average: number
  run: EffluentPath
}

export interface MoistureReading {
  shallow: number
  deep: number
}

export interface AreaData {
  positions: Position[]
  area: number
}

export interface allAreaData {
  raw: AreaData | undefined
  filtered: AreaData | undefined
  mapped: AreaData | undefined
}

export type EffluentTrackerProps = {
  throwDistance: number
  location?: Position
  path?: TimedPosition[]
  spreading?: boolean
  flow?: number
  lastIrrigated?: number
  radius?: number
  runDifference?: number
  missingLocationCnt?: number
  // activePump?: boolean;
  pressureSum?: number //KPa
  lastPressure?: number //KPa
  pressureList: number[]
  currentPumpState?: boolean
  pumpState: boolean[]
  onExclusionArea?: string // exclusion name that the tracker has spread on
  // stoppedAfterSpreading?:boolean;
}

export class EffluentTracker extends DeviceEnabledType {
  constructor(ob: any) {
    super(ob.type || typeName, ob)
    this.throwDistance = ob.throwDistance || DEFAULT_RANGE
    this.location = ob.location
    this.path = ob.path || []
    this.filteredPath = ob.filteredPath
    this.mappedPath = ob.mappedPath
    this.mappedRun = ob.mappedRun
    this.spreading = ob.spreading
    this.flow = ob.flow
    this.pressureSum = ob.pressureSum
    this.lastIrrigated = ob.lastIrrigated
    this.radius = ob.radius
    this.runDifference = ob.runDifference
    this.onExclusionArea = ob.onExclusionArea || ''

    let missing = -1
    if (ob.missingLocationCnt !== undefined) {
      missing = ob.missingLocationCnt
    }
    this.missingLocationCnt = missing
    this.pressureList = ob.pressureList || []
    this.currentPumpState = ob.currentPumpState || true
    this.pumpState = ob.pumpState
    this.moistureReadings = ob.moistureReadings
    this.moistureReadingsList = ob.moistureReadingsList || []
  }

  throwDistance: number
  location?: Position
  path: TimedPosition[]
  filteredPath?: Position[]
  mappedPath?: Position[]
  mappedRun?: string //pk of run
  spreading?: boolean
  flow?: number
  pressureSum?: number //KPa
  latestPressure?: number //KPa
  lastIrrigated?: number
  radius?: number
  runDifference?: number
  missingLocationCnt: number
  pressureList: number[]
  pumpState: boolean[]
  moistureReadings: MoistureReading
  moistureReadingsList: MoistureReading[]

  /** Functions to update spreading path details */
  /**
   * Default method. Accepts all runs
   * Method to get relevant runs
   * @param runList
   * @returns runs of correct run type
   */
  getRelevantRuns(runList: EffluentPath[]): EffluentPath[] {
    debug && console.log('Using default - getRelevantRuns')
    return runList
  }

  /**
   * Filters out outliers in a path
   * Formats datapoints to be valued
   * @returns Updates and returns a filtered version fo the path
   */
  protected filterOutPathOutliers(): TimedPosition[] | undefined {
    debug && console.log('Should be using default - filterOutPathOutliers')
    if (this.path) {
      const filtered = EffluentSpreaderHelper.parseLocationOutliers(this.path)
      this.filteredPath = filtered
      return filtered
    }
  }

  /**
   * Given a set of paths, get the closest run associated with the active data,
   * - Should be for travelling and lateral only
   * @param runs
   * @param path
   * @returns the path closests to the path of the spreader
   */
  getBestMatchedRun(runs: EffluentPath[], path: TimedPosition[] | undefined = this.path): RunStats | undefined {
    debug && console.log('Using default - getBestMatchedRun')

    if (runs.length === 0 || path === undefined || path.length === 0) {
      return undefined
    }

    //turf current path
    const currentPathTurf = EffluentSpreaderHelper.convertPositionToTurf(path).map((tf) => point(tf))

    let runScore: RunStats[] = runs
      .map((run) => {
        let runPoint = run.path
        if (runPoint === undefined || runPoint.length < 2) {
          return undefined
        }
        let turfedRun: Feature<LineString, GeoJsonProperties> | undefined

        turfedRun = lineString(EffluentSpreaderHelper.convertPositionToTurf(runPoint || []))

        let distance = currentPathTurf.reduce((acc: number, cur) => {
          let dis = pointToLineDistance(cur, turfedRun, { units: 'meters' })
          acc += dis
          return acc
        }, 0)

        return {
          distance,
          average: distance / (path.length || 1),
          run,
        }
      })
      .filter((val) => val !== undefined)
      .sort((a, b) => a.distance - b.distance)

    return runScore[0]
  }

  /**
   * Get the area that the tracker most belongs to
   * @param areas the differents areas to check the spreader against
   */
  getRelevantArea(areas: SoilMoistureArea[]): SoilMoistureArea | undefined {
    // let points = this.path ?? this.location ?? undefined;

    let points =
      this.mappedPath && this.mappedPath.length > 0
        ? this.mappedPath
        : this.path && this.path.length > 0
          ? this.path
          : this.location
            ? [this.location]
            : undefined

    if (points && areas.length > 0) {
      const pointsList = EffluentSpreaderHelper.convertPositionToTurf(points)
      const areaCounter = areas
        .map((area) => {
          const areaPolygon = polygon([EffluentSpreaderHelper.convertPositionToTurf(area.points)])

          const count = pointsList.reduce((acc, point) => {
            if (booleanPointInPolygon(point, areaPolygon)) {
              acc++
            }

            return acc
          }, 0)

          return { area, count }
        })
        .filter((val) => val.count !== 0)
        .sort((a, b) => b.count - a.count) //filter out areas that had no intercept, then orders

      if (areaCounter.length > 0) {
        const mostCommon = areaCounter[0].area

        return mostCommon
      }
    }

    return undefined
  }

  /**
   * Given active path data, convert it into datapoints on the mapped run
   * - Should be used for Travelling and Lateral
   * @param path of the Spreader
   * @param run of the EffluentPath

   * @returns
   */
  getPathOnRun(path: Position[], run?: EffluentPath): Position[] {
    debug && console.log('Using default - getPathOnRun')

    if (run) {
      return EffluentSpreaderHelper.getMappedRunPath(path, run.path)
    } else {
      const x = path.map((p) => p.lat)
      const y = path.map((p) => p.lng)
      // let mapped = findLineByLeastSquares(x, y) || path;

      let mapped = EffluentSpreaderHelper.findLineByFirstLast(path) || path

      return mapped
    }
  }

  /**
   * Given a list of paths, returns the most expected run
   * @param runs
   * @returns
   */
  getExpectedRun(runs: EffluentPath[]): { run: EffluentPath; path: Position[] } | undefined {
    debug && console.log('Should be using default - getExpectedRun')

    const relevantRuns = this.getRelevantRuns(runs)
    const filteredPath = this.filterOutPathOutliers() // filtered path is unreliable. When it is, swap out this.path

    const bestMatch = this.getBestMatchedRun(relevantRuns, this.path) // filtered path is unreliable. When it is, swap out this.path
    if (bestMatch !== undefined) {
      const pointsOnRun = this.getPathOnRun(this.path, bestMatch.run) // filtered path is unreliable. When it is, swap out this.path

      // construct acceptable range
      const runDistance = bestMatch.run.throwDistance + bestMatch.run.radius

      if (bestMatch.average < runDistance * 1.2) {
        this.runDifference = bestMatch.distance
        return { run: bestMatch.run, path: pointsOnRun }
      }
    }
  }

  /**
   * Method to reset the spreading details
   *
   * Only to be used after a SpreadingEvent and DeadZone is created
   */
  resetSpreadingDetails() {
    this.path = []
    this.filteredPath = []
    this.spreading = false
    this.mappedRun = ''
    this.mappedPath = []
    this.runDifference = 0
    this.pressureList = []
    this.pumpState = []
    this.moistureReadingsList = []
  }

  /**
   * Get the PK's of the area's that overlap with the spreading event
   * @param areas
   */
  protected getOverlappingAreas(areas: Area[], spreadingArea: Position[]): string[] {
    debug && console.log('Should be using default - getOverlappingAreas')

    const overlapsArr = areas
      .map((paddockArea) => {
        if (paddockArea.points && paddockArea.points.length > 2 && EffluentSpreaderHelper.overlapArea(paddockArea.points, spreadingArea) !== 0) {
          return paddockArea.pk
        }
      })
      .filter((val) => val !== undefined)

    return overlapsArr
  }

  /** Support functions for calculating spread area */

  /**
   * Function to get the spread LatLngs, and area (m^2), of the raw run
   * @returns LatLngs and area (m^2) obj
   */
  getSpreadOfRawRun(relevantRun?: EffluentPath, path: Position[] = this.path): AreaData | undefined {
    debug && console.log('Using default - getSpreadOfRawRun')

    if (path.length > 1) {
      let turfBoundary = EffluentSpreaderHelper.getLineBoundaries(path, this.throwDistance || DEFAULT_RANGE)
      if (turfBoundary) {
        let positions = EffluentSpreaderHelper.convertTurfToPosition(turfBoundary.geometry.coordinates[0])
        let area = EffluentSpreaderHelper.getAreaMeter(turfBoundary)
        return { positions, area }
      }
    }
    return undefined
  }

  /**
   * Function to get the spread LatLngs, and area (m^2), of the filtered run
   * Default to raw if not provided
   * @returns LatLngs and area (m^2) obj
   */
  getSpreadOfFilteredRun(relevantRun?: EffluentPath, path: Position[] = this.filteredPath || this.path): AreaData | undefined {
    debug && console.log('Should be using default - getSpreadOfRawRun')
    return this.getSpreadOfRawRun(relevantRun, path) //relevant is not needed as it is already used
  }

  /**
   * Should be for travelling only
   * Function to get the spread LatLngs, and area (m^2), of the mapped run
   * @returns LatLngs and area (m^2) obj
   */
  getSpreadOfMappedRun(relevantRun?: EffluentPath, path: Position[] = this.mappedPath || this.path): AreaData | undefined {
    debug && console.log('Should be using default - getSpreadOfMappedRun')

    // check if it can even be 'mapped'
    if (path.length > 1) {
      let mappedPoints = path
      if (relevantRun) {
        mappedPoints = this.getPathOnRun(path, relevantRun)
      }
      return this.getSpreadOfRawRun(relevantRun, mappedPoints)
    }
    return undefined
  }

  /** Main functions called outside */

  /**
   *
   * @todo: Make this function that updates the details of the tracker
   */
  // updateTrackerFromActive() {}

  /**
   * Method to update the effluent spreader object
   * Internally updates:
   * - filteredPath (getExpectedRun -> filterOutPathOutliers)
   * - runDifference (getExpectedRun)
   * - mappedRun (this)
   * - mappedPath (this)
   * - last irrigated (this)
   * @param runs
   */
  updateMappedRun(runs: EffluentPath[], lastTimestamp: number) {
    debug && console.log('Should be using default - updateMappedRun')

    const runPath = this.getExpectedRun(runs)
    if (runPath !== undefined) {
      const mappedPathOnRun = this.getPathOnRun(runPath.path, runPath.run)
      this.mappedRun = runPath.run.pk
      this.mappedPath = mappedPathOnRun
    } else {
      // using generic
      this.mappedPath = this.getPathOnRun(this.path)
    }
    this.lastIrrigated = lastTimestamp
  }

  checkOverlapOnExclusions(exclusionList: Area[], previousAreas: DeadZone[]) {
    if (this.path !== undefined) {
      const spreadArea = this.getSpreadOfRawRun(undefined, this.path)
      let overlappedAreas: string[] = []
      if (spreadArea !== undefined) {
        // only check other spreading areas if its more than the first two check-ins, as it may be starting off from a stopped event
        if (this.path.length > 2) {
          // check if it was on th previousAreas
          let previousAreasMatch = previousAreas
            .map((deadZone) => {
              if (deadZone.points.length > 3) {
                let overlapArea: number = EffluentSpreaderHelper.overlapArea(spreadArea.positions, deadZone.points)
                if (overlapArea > ACCEPTABLE_COVERAGE) {
                  return deadZone.name
                }
              }

              return undefined
            })
            .filter((val) => val !== undefined)
          overlappedAreas.push(...previousAreasMatch)
        }

        let exclusionListMatch = exclusionList
          .map((exclusion) => {
            if (exclusion.points.length > 3) {
              let overlapArea: number = EffluentSpreaderHelper.overlapArea(spreadArea.positions, exclusion.points)
              if (overlapArea > ACCEPTABLE_COVERAGE) {
                return exclusion.name
              }
            }

            return undefined
          })
          .filter((val) => val !== undefined)
        overlappedAreas.push(...exclusionListMatch)

        if (overlappedAreas.length > 0) {
          this.onExclusionArea = overlappedAreas.join(', ')
          return
        }
      }
    }
    this.onExclusionArea = undefined
  }

  /**
   * Get run reccorded in effluent object, indicated in key mappedRun
   * @param runs
   * @returns run that matches pk of mappedRun
   */
  getMatchedRun(runs: EffluentPath[]): EffluentPath | undefined {
    debug && console.log('Should be using default - getMatchedRun')

    if (runs && this.mappedRun) {
      let find = runs.find((run) => run.pk === this.mappedRun)
      return find
    }
    return undefined
  }

  /**
   * Function to get the spread LatLngs, and area (m^2), of all 3 conditions
   */
  getSpreadOfAllRuns(relevantRun?: EffluentPath): allAreaData {
    debug && console.log('Should be using default - getSpreadOfAllRuns')
    // spread for raw
    let raw = this.getSpreadOfRawRun(relevantRun)
    // spread for filtered
    let filtered = this.getSpreadOfFilteredRun(relevantRun)
    // spread for mapped
    let mapped = this.getSpreadOfMappedRun(relevantRun)

    return { raw, filtered, mapped }
  }

  /**
   * Create spreading event using the details of this tracker
   */
  createSpreadingEventAndDeadZone(areas: Area[], runs: EffluentPath[]): { Spreading: SpreadingEventType; Dead: DeadZoneType } {
    if (this.path !== undefined && this.path.length !== 0) {
      const startPoint = this.path[0]
      const endPoint = this.path[this.path.length - 1]
      const startTime = startPoint.timestamp ?? 0
      const endTime = endPoint.timestamp ?? 0
      const distance = EffluentSpreaderHelper.measuredDistance(startPoint, endPoint)
      const duration = (endTime - startTime) / Milliseconds.MINUTE
      const amountSpread = duration * (this.flow || 0)
      const deleteTime = getTTLSeconds(duration)

      const mappedRun = this.getMatchedRun(runs)

      const areaMapper = this.getSpreadOfAllRuns(mappedRun)

      const area = areaMapper.mapped?.area ?? areaMapper.filtered?.area ?? areaMapper.raw?.area ?? 1
      const coverage = areaMapper.mapped?.positions ?? areaMapper.filtered?.positions ?? areaMapper.raw?.positions ?? this.path

      // get overlapping areas
      let paddocks = areas.filter((area) => area.subType === undefined || area.subType === AreaSubTypeEnum.Paddock)
      const overlappedAreas = this.getOverlappingAreas(paddocks, coverage)

      const concentration = amountSpread / area
      const irrigatedArea = coverage
      const applicationDepth = (concentration / 1000) * 100 //convert to meters, then to cm

      const pressure =
        this.pressureList.length > 1
          ? this.pressureList.reduce((acc, cur) => {
              acc += cur
              return acc
            }, 0) / this.pressureList.length
          : undefined

      let Spreading: SpreadingEventType = {
        path: this.path,
        duration,
        concentration: concentration,
        irrigatedArea,
        points: irrigatedArea,
        output: amountSpread,
        startTime,
        endTime,
        name: 'SpreadingEvent:' + this.name + '_' + this.lastIrrigated,
        spreaderName: this.name,
        spreaderID: this.id,
        spreaderPK: this.pk, // THIS GOT INCORRECTELY OVEERWRITTEN
        distance,
        area,
        soilRisk: this.soilRisk,
        overlappedAreas,
        mappedRun: this.mappedRun,
        lastActive: endTime,
        runDifference: this.runDifference,
        pressure,
        applicationDepth,
        applicationDepthMM: applicationDepth * 10,
        pressureList: this.pressureList,
        pumpState: this.pumpState,
        moistureReadingsList: this.moistureReadingsList,
        type: 'SpreadingEvent',
      }

      let Dead: DeadZoneType = {
        name: `Event Ended @${this.lastIrrigated}`, // `${effluentTracker.name} Ended @${updatedAt}`,
        path: this.path,
        ttl: deleteTime,
        spreaderID: this.id,
        irrigatedArea,
        filteredPath: this.filteredPath,
        mappedPath: this.mappedPath,
        mappedRun: this.mappedRun,
      }

      return { Spreading, Dead }
    } else {
      throw new Error('No Path data was found, yet a new spreading event was attempted')
    }
  }

  isActive(): boolean {
    if (this.spreading && this.spreading === true && this.path && this.path.length > 0) {
      return true
    }
    return false
  }

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      name: {
        type: 'String',
        editable: true,
        label: 'Name',
        name: 'name',
        description: 'Name of effluent tracker',
        required: true,
      },
      purpose: {
        type: 'String',
        editable: true,
        label: 'Purpose',
        name: 'purpose',
        description: 'Used for',
      },
      throwDistance: {
        type: 'Number',
        editable: true,
        label: 'Throw Radius Length (m)',
        name: 'throwDistance',
        description: 'Throw distance of effluent spreader',
        required: true,
      },
      flow: {
        type: 'Number',
        editable: true,
        label: 'Effluent Flow (L/min)',
        name: 'flow',
        description: 'The rate of liquid L/m',
        required: true,
      },
      location: {
        type: 'RelatedType',
        editable: false,
        label: 'Location',
        name: 'Location',
        description: 'Last position of the effluent spreader',
        objectTypeId: 'Location' as ValidModelType,
        direction: PathDirection.child,
        edgeTypeId: 'EffluentTracker_has_Location',
      },

      comments: {
        type: 'String',
        editable: true,
        label: 'Comments',
        name: 'comments',
        description: 'Free text for info ',
      },
      location_lat: {
        type: 'Number',
        precision: 5,
        editable: false,
        limits: {
          min: -90,
          max: 90,
        },
      },
      location_lng: {
        type: 'Number',
        precision: 5,
        editable: false,
        limits: {
          min: -180,
          max: 180,
        },
      },
    }
  }
}
